import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from 'fflate';
import { boardAssetIds, BoardFileError, parseBoardFile, type BoardFile, type FigmaImportReport } from '@os/doc';
import { isJam } from '../import/jam/detect';

/** Reading limits (plan P9 Ruling 9): announced sizes are checked before anything is inflated. */
export const ARCHIVE_LIMITS = { archiveBytes: 1024 ** 3, jsonBytes: 50 * 1024 ** 2, assetBytes: 40 * 1024 ** 2 } as const;

export class ArchiveError extends Error {
  constructor(readonly code: 'corrupt' | 'invalid' | 'version' | 'too_large') {
    super(code);
    this.name = 'ArchiveError';
  }
}

const BOARD_JSON = 'board.json';
const ext = (mime: string) => (mime === 'image/gif' ? 'gif' : 'webp');

/** `.osboard`: the file (compressed) and one entry per asset blob, stored as is (images are already compressed). */
export async function packBoardArchive(file: BoardFile, blobs: ReadonlyMap<string, Blob>): Promise<Blob> {
  const entries: Zippable = {};
  const assets = await Promise.all(
    file.assets.map(async (a) => {
      const blob = blobs.get(a.id);
      if (!blob) return { ...a, file: null };
      const path = `assets/${a.id}.${ext(a.mime)}`;
      entries[path] = [new Uint8Array(await blob.arrayBuffer()), { level: 0 }];
      return { ...a, file: path };
    }),
  );
  entries[BOARD_JSON] = [strToU8(JSON.stringify({ ...file, assets })), { level: 6 }];
  return new Blob([zipSync(entries) as Uint8Array<ArrayBuffer>], { type: 'application/zip' });
}

function parse(json: string): { file: BoardFile; skipped: number } {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new ArchiveError('invalid');
  }
  try {
    return parseBoardFile(raw);
  } catch (err) {
    if (err instanceof BoardFileError) throw new ArchiveError(err.code);
    throw err;
  }
}

/**
 * A `.osboard` or a `BoardFile` JSON, validated. Images whose asset has neither an archived blob nor a URL are `missing`; an
 * archive entry whose announced size exceeds its limit refuses the whole file before it is inflated.
 */
/** A board file once read: the validated `BoardFile`, its images' contents, and what could not be read. */
export type BoardArchive = { file: BoardFile; skipped: number; blobs: Map<string, Blob>; missing: string[]; /** A FigJam file or a Figma link: what was left out (backlog §12, §23). */ report?: FigmaImportReport };

export async function readBoardArchive(input: File): Promise<BoardArchive> {
  if (input.size > ARCHIVE_LIMITS.archiveBytes) throw new ArchiveError('too_large');
  const bytes = new Uint8Array(await input.arrayBuffer());
  let i = 0;
  while (i < bytes.length && (bytes[i] === 0x20 || bytes[i] === 0x0a || bytes[i] === 0x0d || bytes[i] === 0x09)) i++;
  // A FigJam `.jam` (a zip holding canvas.fig): read in the browser, its code loaded only now.
  if (isJam(bytes)) return (await import('../import/jam/jam-archive')).readJamArchive(bytes);
  const blobs = new Map<string, Blob>();
  let parsed: { file: BoardFile; skipped: number };
  if (bytes[i] === 0x7b) {
    if (bytes.length > ARCHIVE_LIMITS.jsonBytes) throw new ArchiveError('too_large');
    parsed = parse(strFromU8(bytes));
  } else if (bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04) {
    let tooLarge = false;
    let entries: Record<string, Uint8Array>;
    try {
      entries = unzipSync(bytes, {
        filter: (f) => {
          const limit = f.name === BOARD_JSON ? ARCHIVE_LIMITS.jsonBytes : f.name.startsWith('assets/') ? ARCHIVE_LIMITS.assetBytes : -1;
          if (limit < 0) return false;
          if (f.originalSize > limit) tooLarge = true;
          return !tooLarge;
        },
      });
    } catch {
      throw new ArchiveError('corrupt');
    }
    if (tooLarge) throw new ArchiveError('too_large');
    const json = entries[BOARD_JSON];
    if (!json) throw new ArchiveError('invalid');
    parsed = parse(strFromU8(json));
    for (const a of parsed.file.assets) {
      const entry = a.file ? entries[a.file] : undefined;
      // The archive holds the WebP variant (the original for a GIF), whatever the original type was.
      if (entry) blobs.set(a.id, new Blob([entry as Uint8Array<ArrayBuffer>], { type: `image/${ext(a.mime)}` }));
    }
  } else throw new ArchiveError('invalid');
  const assets = parsed.file.assets.map((a) => (blobs.has(a.id) ? a : { ...a, file: null }));
  // Every image the objects use without content: unlisted (or invalid) in the manifest, or listed with neither.
  const listed = new Map(assets.map((a) => [a.id, a]));
  const missing = boardAssetIds(parsed.file.objects).filter((id) => !blobs.has(id) && !listed.get(id)?.url);
  return { file: { ...parsed.file, assets }, skipped: parsed.skipped, blobs, missing };
}
